import { LoadingRows } from "../UI/Loading/LoadingRows.tsx";
import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/it";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Modal } from "../UI/Modal/Modal.tsx";
import { ErrorFeedback } from "../UI/Feedback/ErrorFeedback.tsx";
import { Switch } from "../UI/Switch.tsx";
import {
  CloserMisalignedSlotsDTO,
  MisalignedCalendarSlotDTO,
  MisalignedBoundary,
  MisalignedWorkingHoursDTO,
  useGetHubSpotCloserMisalignedSlots,
} from "../HubSpot/useGetHubSpotCloserMisalignedSlots.ts";
import { misalignedSlotsRange } from "./closerIssues.ts";

const TZ = "Europe/Rome";

type CloserSlotsOfDay = {
  closer: CloserMisalignedSlotsDTO;
  slots: MisalignedCalendarSlotDTO[];
};

type DayGroup = {
  dayKey: string;
  closers: CloserSlotsOfDay[];
  slotCount: number;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

function nameFromEmail(email: string): string {
  return email
    .split("@")[0]
    .split(".")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

function closerName(closer: CloserMisalignedSlotsDTO): string {
  const fullName = [closer.firstName, closer.lastName]
    .filter((p) => p && p.trim().length > 0)
    .join(" ");
  return fullName.length > 0 ? fullName : nameFromEmail(closer.email);
}

const WEEK_DAYS: { key: string; label: string }[] = [
  { key: "MONDAY", label: "Lun" },
  { key: "TUESDAY", label: "Mar" },
  { key: "WEDNESDAY", label: "Mer" },
  { key: "THURSDAY", label: "Gio" },
  { key: "FRIDAY", label: "Ven" },
  { key: "SATURDAY", label: "Sab" },
  { key: "SUNDAY", label: "Dom" },
];

function formatDays(days: string[]): string {
  const indexes = WEEK_DAYS.flatMap((d, i) =>
    days.includes(d.key) ? [i] : [],
  );
  if (indexes.length === WEEK_DAYS.length) return "Tutti i giorni";

  const runs: number[][] = [];
  for (const index of indexes) {
    const last = runs[runs.length - 1];
    if (last && last[last.length - 1] === index - 1) last.push(index);
    else runs.push([index]);
  }

  return runs
    .map((run) =>
      run.length >= 3
        ? `${WEEK_DAYS[run[0]].label}–${WEEK_DAYS[run[run.length - 1]].label}`
        : run.map((i) => WEEK_DAYS[i].label).join(", "),
    )
    .join(", ");
}

function formatMinuteOfDay(minute: number): string {
  const hours = Math.floor(minute / 60);
  return `${String(hours).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;
}

function boundaryLabel(boundary: MisalignedBoundary): string {
  switch (boundary) {
    case "start":
      return "Inizio";
    case "end":
      return "Fine";
    default:
      return "Inizio e fine";
  }
}

function isBoundaryMisaligned(
  boundary: MisalignedBoundary,
  side: "start" | "end",
): boolean {
  return boundary === "both" || boundary === side;
}

function groupByDay(closers: CloserMisalignedSlotsDTO[]): DayGroup[] {
  const byDay = new Map<string, Map<string, CloserSlotsOfDay>>();

  for (const closer of closers) {
    for (const slot of closer.slots) {
      const dayKey = dayjs(slot.start).tz(TZ).format("YYYY-MM-DD");

      if (!byDay.has(dayKey)) byDay.set(dayKey, new Map());
      const closersOfDay = byDay.get(dayKey)!;

      if (!closersOfDay.has(closer.id)) {
        closersOfDay.set(closer.id, { closer, slots: [] });
      }
      closersOfDay.get(closer.id)!.slots.push(slot);
    }
  }

  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dayKey, closersOfDay]) => {
      const closersList = [...closersOfDay.values()].sort((a, b) =>
        a.closer.email.localeCompare(b.closer.email),
      );
      return {
        dayKey,
        closers: closersList,
        slotCount: closersList.reduce((sum, c) => sum + c.slots.length, 0),
      };
    });
}

function CloserHeader({ closer }: { closer: CloserMisalignedSlotsDTO }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <p className="text-sm font-medium">{closerName(closer)}</p>
      <p className="text-xs text-gray-400 truncate">{closer.email}</p>
    </div>
  );
}

function WorkingHoursRow({ entry }: { entry: MisalignedWorkingHoursDTO }) {
  const badClass = "text-red-600 font-semibold";
  const okClass = "text-gray-600";
  return (
    <div className="flex items-baseline justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm tabular-nums">
          <span
            className={
              isBoundaryMisaligned(entry.misalignedBoundary, "start")
                ? badClass
                : okClass
            }
          >
            {formatMinuteOfDay(entry.startMinute)}
          </span>
          <span className="text-gray-400"> – </span>
          <span
            className={
              isBoundaryMisaligned(entry.misalignedBoundary, "end")
                ? badClass
                : okClass
            }
          >
            {formatMinuteOfDay(entry.endMinute)}
          </span>
        </p>
        <p className="text-xs text-gray-400 italic truncate">
          {formatDays(entry.days)}
        </p>
      </div>
      <span className="shrink-0 text-xs px-1.5 py-0.5 rounded bg-sky-100 text-sky-700">
        {boundaryLabel(entry.misalignedBoundary)}
      </span>
    </div>
  );
}

export function MisalignedSlotsModal({ isOpen, onClose }: Props) {
  const { getHubSpotCloserMisalignedSlots, loading } =
    useGetHubSpotCloserMisalignedSlots();
  const [closers, setClosers] = useState<CloserMisalignedSlotsDTO[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [includeSecondaryCalendars, setIncludeSecondaryCalendars] =
    useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { from, to } = misalignedSlotsRange();
      const data = await getHubSpotCloserMisalignedSlots({
        fromIncl: from.toDate(),
        toIncl: to.toDate(),
        includeSecondaryCalendars,
      });
      setClosers(data ?? []);
    } catch (err) {
      console.error("Error fetching misaligned slots:", err);
      setError("Errore nel recupero degli slot non allineati. Riprova.");
      setClosers([]);
    }
  }, [getHubSpotCloserMisalignedSlots, includeSecondaryCalendars]);

  useEffect(() => {
    if (!isOpen) return;
    load();
  }, [isOpen, load]);

  const slotClosers = closers.filter((c) => c.slots.length > 0);
  const workingHoursClosers = closers.filter((c) => c.workingHours.length > 0);
  const totalSlots = slotClosers.reduce((sum, c) => sum + c.slots.length, 0);
  const dayGroups = groupByDay(slotClosers);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Slot non allineati"
      overridePanel={({ title, children }) => (
        <Dialog.Panel className="relative w-full max-w-3xl transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            className="absolute top-3 right-3 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          <h3 className="text-lg font-medium leading-6 text-gray-900 pr-8">
            {title}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Closer con eventi (o working hours) che non iniziano o non finiscono
            al minuto :00 o :30 nei prossimi 7 giorni.
          </p>
          <div className="mt-3 flex justify-end">
            <Switch
              label="Includi calendari secondari"
              checked={includeSecondaryCalendars}
              onChange={setIncludeSecondaryCalendars}
            />
          </div>
          <div className="mt-4">{children}</div>
        </Dialog.Panel>
      )}
    >
      {loading && <LoadingRows rows={4} />}

      {!loading && error && <ErrorFeedback title="Errore" message={error} />}

      {!loading && !error && closers.length === 0 && (
        <p className="text-sm text-gray-500">
          Nessun disallineamento trovato. 🎉
        </p>
      )}

      {!loading && !error && closers.length > 0 && (
        <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
          {slotClosers.length > 0 && (
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              {dayGroups.length} giorni · {slotClosers.length} closer ·{" "}
              {totalSlots} slot non allineati
            </p>
          )}

          {dayGroups.map((day) => (
            <div key={day.dayKey}>
              <div className="flex items-baseline justify-between mb-1.5">
                <p className="text-sm font-semibold capitalize">
                  {dayjs(day.dayKey).locale("it").format("dddd D MMMM")}
                </p>
                <span className="text-xs text-gray-400">
                  {day.slotCount} slot · {day.closers.length} closer
                </span>
              </div>

              <div className="rounded-md border border-gray-200 divide-y divide-gray-200 overflow-hidden">
                {day.closers.map(({ closer, slots }) => (
                  <div key={closer.id} className="px-3 py-2">
                    <CloserHeader closer={closer} />

                    <div className="mt-1 space-y-1">
                      {slots.map((slot, index) => {
                        const start = dayjs(slot.start).tz(TZ);
                        const end = dayjs(slot.end).tz(TZ);
                        const badClass = "text-red-600 font-semibold";
                        const okClass = "text-gray-600";
                        return (
                          <div
                            key={`${slot.start}-${slot.end}-${index}`}
                            className="flex items-baseline justify-between gap-3"
                          >
                            <div className="min-w-0">
                              <p className="text-sm tabular-nums">
                                <span
                                  className={
                                    isBoundaryMisaligned(
                                      slot.misalignedBoundary,
                                      "start",
                                    )
                                      ? badClass
                                      : okClass
                                  }
                                >
                                  {start.format("HH:mm")}
                                </span>
                                <span className="text-gray-400"> – </span>
                                <span
                                  className={
                                    isBoundaryMisaligned(
                                      slot.misalignedBoundary,
                                      "end",
                                    )
                                      ? badClass
                                      : okClass
                                  }
                                >
                                  {end.format("HH:mm")}
                                </span>
                              </p>
                              <p className="text-xs text-gray-400 italic truncate">
                                {slot.title}
                                {slot.calendarName
                                  ? ` · calendario: ${slot.calendarName}`
                                  : ""}
                              </p>
                            </div>
                            <span className="shrink-0 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                              {boundaryLabel(slot.misalignedBoundary)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {workingHoursClosers.length > 0 && (
            <div>
              <div className="flex items-baseline justify-between mb-1.5">
                <p className="text-sm font-semibold">
                  Working hours HubSpot non allineate
                </p>
                <span className="text-xs text-gray-400">
                  {workingHoursClosers.length} closer
                </span>
              </div>

              <div className="rounded-md border border-sky-200 divide-y divide-sky-100 overflow-hidden">
                {workingHoursClosers.map((closer) => (
                  <div key={closer.id} className="px-3 py-2">
                    <CloserHeader closer={closer} />

                    <div className="mt-1 space-y-1">
                      {closer.workingHours.map((entry, index) => (
                        <WorkingHoursRow
                          key={`${entry.days.join()}-${entry.startMinute}-${entry.endMinute}-${index}`}
                          entry={entry}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
